import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Smartphone,
  CreditCard,
  Award,
  Flag,
  Sliders,
  Terminal,
  FileKey,
  ShieldCheck,
  Activity,
  LogOut,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../../lib/utils.js';
import { useAuth } from '../../context/AuthContext.js';
import { useI18n } from '../../context/I18nContext.js';
import { RoleBadge } from '../ui/Badge.js';

interface NavItemConfig {
  key:
    | 'overview'
    | 'users'
    | 'devices'
    | 'subscriptions'
    | 'entitlements'
    | 'features'
    | 'config'
    | 'commands'
    | 'snapshots'
    | 'audit'
    | 'system';
  path: string;
  icon: React.ElementType;
}

const NAV_CONFIG: NavItemConfig[] = [
  { key: 'overview', path: '/admin', icon: LayoutDashboard },
  { key: 'users', path: '/admin/users', icon: Users },
  { key: 'devices', path: '/admin/devices', icon: Smartphone },
  { key: 'subscriptions', path: '/admin/subscriptions', icon: CreditCard },
  { key: 'entitlements', path: '/admin/entitlements', icon: Award },
  { key: 'features', path: '/admin/features', icon: Flag },
  { key: 'config', path: '/admin/config', icon: Sliders },
  { key: 'commands', path: '/admin/commands', icon: Terminal },
  { key: 'snapshots', path: '/admin/snapshots', icon: FileKey },
  { key: 'audit', path: '/admin/audit', icon: ShieldCheck },
  { key: 'system', path: '/admin/system', icon: Activity },
];

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const { admin, logout } = useAuth();
  const { t } = useI18n();

  return (
    <aside
      className={cn(
        'relative flex flex-col border-r border-slate-200 dark:border-obsidian-750 bg-white dark:bg-obsidian-950 transition-all duration-300 z-30 select-none',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Brand Header */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200 dark:border-obsidian-750">
        {!collapsed ? (
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white shadow-sm font-bold text-sm">
              H
            </div>
            <div>
              <span className="font-bold text-sm tracking-tight text-slate-900 dark:text-slate-100">
                Hisob Admin
              </span>
              <span className="block text-[10px] text-slate-500 font-mono">
                {t('nav.consoleTag')}
              </span>
            </div>
          </div>
        ) : (
          <div className="w-8 h-8 mx-auto rounded-lg bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">
            H
          </div>
        )}

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-obsidian-850 transition-colors"
          title={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {NAV_CONFIG.map((item) => {
          const Icon = item.icon;
          const label = t(`nav.${item.key}`);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/admin'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors group relative',
                  isActive
                    ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-semibold'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-obsidian-850 hover:text-slate-900 dark:hover:text-slate-200'
                )
              }
              title={collapsed ? label : undefined}
            >
              <Icon className="w-4 h-4 shrink-0 transition-transform group-hover:scale-110" />
              {!collapsed && <span>{label}</span>}
              {collapsed && (
                <div className="absolute left-full ml-2 px-2.5 py-1 bg-obsidian-900 border border-obsidian-750 text-white text-xs rounded shadow-lg whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
                  {label}
                </div>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Admin Profile & Logout Section */}
      <div className="p-3 border-t border-slate-200 dark:border-obsidian-750 bg-slate-50/70 dark:bg-obsidian-900/60">
        {!collapsed ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-obsidian-800 flex items-center justify-center text-slate-700 dark:text-slate-200 font-semibold text-xs shrink-0">
                {admin?.email.slice(0, 2).toUpperCase() || 'AD'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-900 dark:text-slate-100 truncate">
                  {admin?.email || 'admin@hisob.uz'}
                </p>
                {admin?.role && <RoleBadge role={admin.role} />}
              </div>
            </div>

            <button
              onClick={logout}
              className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>{t('nav.signOut')}</span>
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-obsidian-800 flex items-center justify-center text-xs font-bold text-slate-700 dark:text-slate-200">
              {admin?.email.slice(0, 1).toUpperCase() || 'A'}
            </div>
            <button
              onClick={logout}
              className="p-2 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition-colors"
              title={t('nav.signOut')}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
